import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Check, FileText, Plus, Sparkles, Upload, X,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui';
import { STAT_CARD_BASE, STAT_CHIP } from '../../components/ui/StatCard';
import { INDUSTRY_PRESETS, AUDIENCE_LEVELS, presetFor } from '../../fixtures/industryPresets';
import simulationService from '../../services/simulationService';
import { showError } from '../../utils/toast';
import { freeMetricSlot } from '../../utils/simulationEngine';

/**
 * Simulation intake.
 *
 * Two ways in:
 *   - "I'll write the rounds" (guided, the default). Follows the client's flow:
 *       1 Overview of the case → 2 The numbers (metric, unit, what's ideal) →
 *       3 The teams → 4 The rounds → 5 Decision deck → run.
 *     The trainer writes steps 1-4; the AI writes the decision deck (each team's
 *     options), each team's point of view in every round, the outside events and the
 *     debrief. Step 5 happens in the builder, where the trainer reviews and edits the
 *     deck before running it.
 *   - "Draft it all for me" (prompt-only). The brief alone; the AI designs the whole
 *     frame. A starting point the trainer then edits in the builder.
 *
 * Output is the `intake` object the generation pipeline consumes. Trainer-written
 * rounds travel as `rounds.items`; the server keeps their words verbatim.
 */

const STEPS = [
  { n: 1, label: 'Overview' },
  { n: 2, label: 'The numbers' },
  { n: 3, label: 'The teams' },
  { n: 4, label: 'The rounds' },
  // Written by the AI from steps 1-4, then reviewed in the builder — never a form here.
  { n: 5, label: 'Decision deck' },
];

/** The last step the trainer fills in on this page; the decision deck comes after. */
const LAST_FORM_STEP = 4;

const VARIANT_CYCLE = ['blue', 'violet', 'emerald', 'amber', 'rose', 'teal'];

/** The storage engine scores at most five metrics; see simulationEngine.KPI_KEYS. */
const MAX_METRICS = 5;
const MIN_ROUNDS = 2;
const MAX_ROUNDS = 6;

const blankRound = () => ({ title: '', context: '', challenge: '' });

/** '' stays empty (no target); anything else becomes a number. */
const numberOrNull = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

const SimulationIntakePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * The default is the guided path: trainers write their own rounds. It pre-fills
   * teams and metrics from an industry preset, so steps 2 and 3 are shown rather than
   * skipped — a trainer who typed a banking brief sees the manufacturing defaults and
   * changes them, instead of discovering them in the finished simulation.
   */
  const [promptOnly, setPromptOnly] = useState(false);

  const [form, setForm] = useState({
    brief: '',
    industryKey: 'manufacturing',
    industryOther: '',
    region: 'India',
    audienceLevel: 'supervisor',
    headcount: '20-40',
    functions: presetFor('manufacturing').functions,
    kpis: presetFor('manufacturing').kpis,
    rounds: [blankRound(), blankRound(), blankRound()],
    minutesPerRound: 5,
    negotiateEnabled: true,
    negotiateMinutes: 3,
    documents: [],
  });

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  /** Changing industry re-seeds teams and numbers — unless the trainer has edited them. */
  const chooseIndustry = (key) => {
    const preset = presetFor(key);
    patch({ industryKey: key, functions: preset.functions, kpis: preset.kpis });
  };

  /** Problems on one step, keyed by field. Empty object = the step is fine. */
  const problemsOn = (n) => {
    const e = {};
    if (n === 1) {
      if (!form.brief.trim()) e.brief = 'Tell us what you want people to practise — this is the one thing we genuinely need.';
      else if (form.brief.trim().length < 15) e.brief = 'A little more detail gives a much better simulation.';
    }
    if (n === 3) {
      if (form.functions.filter((f) => f.name.trim()).length < 2) e.teams = 'Name at least two teams — with one there is nobody to disagree with.';
      else if (form.functions.some((f) => !f.name.trim())) e.teams = 'Give every team a name, or remove the empty one.';
    }
    if (n === 2) {
      if (form.kpis.length < 3) e.metrics = 'Use at least three metrics, so every choice has a trade-off.';
      else if (form.kpis.some((k) => !String(k.label || '').trim())) e.metrics = 'Give every metric a name, or remove the empty one.';
      else if (form.kpis.some((k) => !(Number(k.cap) > 0))) e.metrics = 'Every metric needs a cap above zero.';
    }
    if (n === 4) {
      form.rounds.forEach((r, i) => {
        if (!r.title.trim()) e[`round_${i}`] = 'Give this round a title.';
        else if (r.context.trim().length < 20) e[`round_${i}`] = 'Describe the situation in a sentence or two.';
      });
    }
    return e;
  };

  /** Validate one step and show its problems. */
  const checkStep = (n) => {
    const e = problemsOn(n);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!checkStep(step)) return;
    setStep(Math.min(LAST_FORM_STEP, step + 1));
  };

  const generate = async () => {
    if (promptOnly) {
      if (!checkStep(1)) return;
    } else {
      // Land the trainer on the first step with a problem rather than toasting a list.
      const bad = [1, 2, 3, 4].find((n) => Object.keys(problemsOn(n)).length > 0);
      if (bad) { setStep(bad); checkStep(bad); return; }
    }
    setGenerating(true);
    try {
      const industryLabel =
        form.industryKey === 'other'
          ? form.industryOther
          : INDUSTRY_PRESETS.find((p) => p.key === form.industryKey)?.label || '';

      /**
       * Prompt-only sends the brief and NOTHING else — no industry, no teams, no
       * metrics, no round count. Sending them "just in case" is exactly how every
       * simulation came out shaped like the manufacturing preset the form defaults to.
       */
      const payload = promptOnly
        ? { brief: form.brief.trim(), mode: 'promptOnly', documentIds: form.documents.map((d) => d.id) }
        : {
            brief: form.brief.trim(),
            industry: industryLabel,
            region: form.region,
            audience: { level: form.audienceLevel, headcount: form.headcount },
            functions: form.functions.map((f) => ({ ...f, name: f.name.trim(), mandate: f.mandate.trim() })),
            kpis: form.kpis.map((k) => ({
              ...k,
              label: String(k.label).trim(),
              cap: Number(k.cap),
              start: numberOrNull(k.start) ?? 0,
              target: numberOrNull(k.target),
            })),
            rounds: {
              count: form.rounds.length,
              minutesPerRound: form.minutesPerRound,
              // The trainer's own rounds. The server keeps these words verbatim and the
              // AI only adds what turns each into a playable round.
              items: form.rounds.map((r) => ({
                title: r.title.trim(),
                context: r.context.trim(),
                challenge: r.challenge.trim(),
              })),
            },
            negotiateEnabled: form.negotiateEnabled,
            negotiateMinutes: form.negotiateMinutes,
            documentIds: form.documents.map((d) => d.id),
          };

      const created = await simulationService.create(payload);

      // Kick off the AI pipeline, then hand over to the builder, which renders live
      // progress. Generation takes ~2 minutes; nobody should sit on a spinner here.
      try {
        await simulationService.generate(created.simulation.id);
      } catch (err) {
        // No AI configured, or the service is down. The draft still exists with the
        // trainer's teams and metrics, so send them there rather than losing the input.
        showError(err.userMessage || err.message || 'Saved your brief, but could not start writing');
      }
      navigate(`/trainer/simulations/${created.simulation.id}${promptOnly ? '' : '?step=deck'}`);
    } catch (err) {
      showError(err.userMessage || 'Could not draft the simulation');
      setGenerating(false);
    }
  };

  // ---------- team editing ----------
  const addFunction = () => {
    const i = form.functions.length;
    patch({
      functions: [
        ...form.functions,
        { key: `team_${i + 1}`, name: '', variant: VARIANT_CYCLE[i % VARIANT_CYCLE.length], mandate: '', measuredOn: [] },
      ],
    });
  };
  const removeFunction = (i) => patch({ functions: form.functions.filter((_, x) => x !== i) });
  const changeFunction = (i, field, value) =>
    patch({ functions: form.functions.map((f, x) => (x === i ? { ...f, [field]: value } : f)) });

  // ---------- kpi editing ----------
  const addKpi = () => {
    // A new metric takes a real storage slot. An invented key (the old `metric_6`) was
    // a metric no card could ever move, so it sat at zero for the whole session.
    const slot = freeMetricSlot(form.kpis);
    if (!slot) return;
    const i = form.kpis.length;
    patch({
      kpis: [
        ...form.kpis,
        { key: slot, label: '', unit: 'points', direction: 'higher', cap: 50, normaliseAbs: false, variant: VARIANT_CYCLE[i % VARIANT_CYCLE.length] },
      ],
    });
  };
  const removeKpi = (i) => patch({ kpis: form.kpis.filter((_, x) => x !== i) });
  const changeKpi = (i, field, value) =>
    patch({
      kpis: form.kpis.map((k, x) => {
        if (x !== i) return k;
        // "Balance it" metrics are read as distance from the start either way.
        if (field === 'direction') return { ...k, direction: value, normaliseAbs: value === 'neutral' };
        return { ...k, [field]: value };
      }),
    });

  // ---------- round editing ----------
  const addRound = () => {
    if (form.rounds.length >= MAX_ROUNDS) return;
    patch({ rounds: [...form.rounds, blankRound()] });
  };
  const removeRound = (i) => {
    if (form.rounds.length <= MIN_ROUNDS) return;
    patch({ rounds: form.rounds.filter((_, x) => x !== i) });
    setErrors({});
  };
  const changeRound = (i, field, value) => {
    patch({ rounds: form.rounds.map((r, x) => (x === i ? { ...r, [field]: value } : r)) });
    if (errors[`round_${i}`]) setErrors((e) => { const n = { ...e }; delete n[`round_${i}`]; return n; });
  };

  const inputCls =
    'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:ring-primary-500/20';

  return (
    <DashboardLayout title="New simulation">
      <div className="w-full px-4 lg:px-6 py-6 max-w-3xl mx-auto pb-20 sm:pb-6 space-y-5">

        {/* ---------- how much do you want to decide? ---------- */}
        <div className={`${STAT_CARD_BASE} p-2`}>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { on: false, label: 'I\'ll write the rounds', hint: 'You write the situation and rounds; the AI writes the options' },
              { on: true, label: 'Draft it all for me', hint: 'The AI designs everything from your brief, for you to edit' },
            ].map((opt) => (
              <button
                key={String(opt.on)}
                type="button"
                onClick={() => { setPromptOnly(opt.on); setStep(1); setErrors({}); }}
                aria-pressed={promptOnly === opt.on}
                className={`rounded-xl px-4 py-3 text-left transition-all ${
                  promptOnly === opt.on
                    ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                    : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                <span className="block text-sm font-semibold">{opt.label}</span>
                <span className={`mt-0.5 block text-xs ${promptOnly === opt.on ? 'opacity-70' : 'text-neutral-400'}`}>
                  {opt.hint}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ---------- progress (guided only) ---------- */}
        {!promptOnly && (
        <div className={`${STAT_CARD_BASE} p-5`}>
          <div className="flex items-center justify-between">
            {STEPS.map((s) => {
              const done = s.n < step;
              const active = s.n === step;
              return (
                <div key={s.n} className="flex min-w-0 flex-1 items-center">
                  <button
                    type="button"
                    onClick={() => s.n < step && setStep(s.n)}
                    disabled={s.n > step}
                    className="flex min-w-0 items-center text-left disabled:cursor-default"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ring-1 ring-inset transition-all ${
                        done
                          ? 'bg-emerald-500 text-white ring-emerald-500/40'
                          : active
                          ? 'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30'
                          : 'bg-neutral-50 text-neutral-400 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700'
                      }`}
                    >
                      {done ? <Check size={15} strokeWidth={2.5} /> : s.n}
                    </span>
                    <span
                      className={`ml-2.5 hidden truncate text-sm font-medium sm:block ${
                        active ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                  {s.n < STEPS.length && (
                    <span className={`mx-2 h-0.5 flex-1 rounded-full sm:mx-3 ${done ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-neutral-800'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* ================= PROMPT-ONLY: one field, nothing else ================= */}
        {promptOnly && (
          <div className={`${STAT_CARD_BASE} space-y-5 p-5 sm:p-6`}>
            <div>
              <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                What do you want people to practise?
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Write it as you&apos;d say it to a colleague. Behaviour, not topic — &ldquo;our
                shift leads blame each other when the line goes down&rdquo; beats &ldquo;operations
                training&rdquo;. Mention the industry, who&apos;s in the room and anything that
                should show up, and it will all be used.
              </p>
              <textarea
                rows={7}
                value={form.brief}
                onChange={(e) => { patch({ brief: e.target.value }); if (errors.brief) setErrors({}); }}
                placeholder="e.g. Our regional sales managers undercut each other on price to hit their own quota, and the company loses margin. I want them to feel what that does to the business, and to practise negotiating instead of racing to the bottom."
                className={`${inputCls} mt-3 resize-y`}
              />
              {errors.brief && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{errors.brief}</p>}
            </div>

            <div className="rounded-xl bg-neutral-50 px-4 py-3 dark:bg-neutral-800/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Worked out from your brief
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                The industry and setting · who the teams are and how many · what each team is
                judged on, in its own units · how many rounds the story needs · the scenarios,
                every team&apos;s options, the outside pressures and the debrief questions.
                Nothing is filled in from a template. You can edit all of it afterwards.
              </p>
            </div>
          </div>
        )}

        {/* No mode="wait" / no exit — see the note in SimulationRehearsePage; a phase
            whose mount depends on an animation callback can wedge on a hidden tab. */}
        {!promptOnly && (
        <AnimatePresence>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18 }}
          >
            {/* ================= STEP 1 ================= */}
            {step === 1 && (
              <div className={`${STAT_CARD_BASE} space-y-5 p-5 sm:p-6`}>
                <div>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Give an overview of the case
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    The organisation, what is going wrong, and the behaviour you want people to practise.
                    Behaviour, not topic — &ldquo;stop blaming each other when the line goes
                    down&rdquo; beats &ldquo;operations training&rdquo;. You&apos;ll write each round
                    in the last step.
                  </p>
                  <textarea
                    rows={4}
                    value={form.brief}
                    onChange={(e) => { patch({ brief: e.target.value }); if (errors.brief) setErrors({}); }}
                    placeholder="e.g. A packaging plant running three shifts. Shift leads blame other departments instead of solving problems together. I want them to feel what that costs the plant."
                    className={`${inputCls} mt-3 resize-y`}
                  />
                  {errors.brief && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{errors.brief}</p>}
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Industry</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INDUSTRY_PRESETS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => chooseIndustry(p.key)}
                        className={`rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                          form.industryKey === p.key
                            ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                            : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => chooseIndustry('other')}
                      className={`rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                        form.industryKey === 'other'
                          ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                          : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                      }`}
                    >
                      Something else
                    </button>
                  </div>
                  {form.industryKey === 'other' && (
                    <input
                      value={form.industryOther}
                      onChange={(e) => patch({ industryOther: e.target.value })}
                      placeholder="Describe the industry — e.g. commercial aviation ground operations"
                      className={`${inputCls} mt-2`}
                    />
                  )}
                  <p className="mt-2 text-xs text-neutral-400">
                    Sets the starting teams and numbers, and tells the AI which job titles,
                    equipment and regulations to use. All editable.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Region</label>
                    <input value={form.region} onChange={(e) => patch({ region: e.target.value })} className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Who&apos;s in the room</label>
                    <select value={form.audienceLevel} onChange={(e) => patch({ audienceLevel: e.target.value })} className={`${inputCls} mt-2`}>
                      {AUDIENCE_LEVELS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ================= STEP 3: the teams ================= */}
            {step === 3 && (
              <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                  Which teams collide?
                </h3>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  Participants play a team, not themselves. The tension comes from each team being
                  judged on a different number — so give them mandates that genuinely pull apart.
                </p>

                <div className="mt-4 space-y-2.5">
                  {form.functions.map((fn, i) => (
                    <div key={i} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${STAT_CHIP[fn.variant]?.split(' ')[0] || 'bg-neutral-300'}`} />
                        <div className="min-w-0 flex-1 space-y-2">
                          <input
                            value={fn.name}
                            onChange={(e) => { changeFunction(i, 'name', e.target.value); if (errors.teams) setErrors({}); }}
                            placeholder="Team name — e.g. Maintenance"
                            className={inputCls}
                          />
                          <input
                            value={fn.mandate}
                            onChange={(e) => changeFunction(i, 'mandate', e.target.value)}
                            placeholder="Their mandate in one line — e.g. Keep the machines alive."
                            className={inputCls}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFunction(i)}
                          disabled={form.functions.length <= 2}
                          className="mt-1 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-neutral-800"
                          aria-label={`Remove ${fn.name || 'team'}`}
                        >
                          <X size={15} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addFunction}
                  disabled={form.functions.length >= 6}
                  className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2.5 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 disabled:opacity-40 dark:border-neutral-700 dark:hover:border-neutral-600"
                >
                  <Plus size={14} strokeWidth={2} className="mr-1 inline" />
                  Add a team
                </button>
                {errors.teams && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{errors.teams}</p>}
                <p className="mt-2 text-xs text-neutral-400">
                  Four is the sweet spot. Fewer than three and there&apos;s little cross-pressure; more
                  than six and the reveal gets hard to read on a projector.
                </p>
              </div>
            )}

            {/* ================= STEP 2: the numbers ================= */}
            {step === 2 && (
              <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                  Which numbers does this business live or die by?
                </h3>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  Every decision moves these. They&apos;re what makes a trade-off feel real rather
                  than academic. Three to five works best.
                </p>

                <div className="mt-4 space-y-2.5">
                  {form.kpis.map((k, i) => (
                    <div key={k.key} className="flex items-start gap-2 rounded-xl border border-neutral-200 p-3 dark:border-neutral-700/60">
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="grid gap-2 sm:grid-cols-[1.4fr_1fr_1fr]">
                          <input value={k.label} onChange={(e) => { changeKpi(i, 'label', e.target.value); if (errors.metrics) setErrors({}); }} placeholder="Metric" aria-label="Metric name" className={inputCls} />
                          <input value={k.unit} onChange={(e) => changeKpi(i, 'unit', e.target.value)} placeholder="Unit" aria-label="Unit" className={inputCls} />
                          <select value={k.direction} onChange={(e) => changeKpi(i, 'direction', e.target.value)} aria-label="Which way is better" className={inputCls}>
                            <option value="lower">Lower is better</option>
                            <option value="higher">Higher is better</option>
                            <option value="neutral">Balance it</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <label className="block">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Starts at</span>
                            <input
                              type="number"
                              value={k.start ?? ''}
                              onChange={(e) => changeKpi(i, 'start', e.target.value)}
                              placeholder="0"
                              className={`${inputCls} mt-1`}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                              {k.direction === 'neutral' ? 'Keep within ±' : 'Target'} <span className="normal-case tracking-normal">(optional)</span>
                            </span>
                            <input
                              type="number"
                              value={k.target ?? ''}
                              onChange={(e) => changeKpi(i, 'target', e.target.value)}
                              placeholder="None"
                              className={`${inputCls} mt-1`}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Cap</span>
                            <input
                              type="number"
                              value={k.cap}
                              onChange={(e) => changeKpi(i, 'cap', e.target.value === '' ? '' : Number(e.target.value))}
                              placeholder="Cap"
                              className={`${inputCls} mt-1`}
                            />
                          </label>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeKpi(i)}
                        disabled={form.kpis.length <= 3}
                        className="mt-1 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-neutral-800"
                        aria-label={`Remove ${k.label || 'metric'}`}
                      >
                        <X size={15} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addKpi}
                  disabled={form.kpis.length >= MAX_METRICS}
                  className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2.5 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 disabled:opacity-40 dark:border-neutral-700 dark:hover:border-neutral-600"
                >
                  <Plus size={14} strokeWidth={2} className="mr-1 inline" />
                  {form.kpis.length >= MAX_METRICS ? `Five metrics is the most a simulation can score` : 'Add a metric'}
                </button>
                {errors.metrics && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{errors.metrics}</p>}
                <p className="mt-2 text-xs leading-relaxed text-neutral-400">
                  <span className="font-medium">Starts at</span> is where the business is today.{' '}
                  <span className="font-medium">Target</span> is optional — leave it empty and only the
                  direction counts; set it and the results show whether the room reached it.{' '}
                  <span className="font-medium">Cap</span> is the biggest realistic change across the
                  whole session — it scales the bars, nothing else. Rough numbers are fine.
                </p>
              </div>
            )}

            {/* ================= STEP 4: the rounds ================= */}
            {step === 4 && (
              <div className="space-y-4">
                <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Write the rounds (caselets)
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    The caselets the room plays: one situation per round, in order, each harder than
                    the last. The AI keeps your words, then writes the decision deck — each team&apos;s
                    options — and each team&apos;s point of view. You review it all in step 5.
                  </p>

                  <div className="mt-4 space-y-3">
                    {form.rounds.map((r, i) => (
                      <div
                        key={i}
                        className={`rounded-xl border p-3.5 ${
                          errors[`round_${i}`] ? 'border-red-300 dark:border-red-500/40' : 'border-neutral-200 dark:border-neutral-700/60'
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          <span className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-[11px] font-semibold text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1 space-y-2">
                            <input
                              value={r.title}
                              onChange={(e) => changeRound(i, 'title', e.target.value)}
                              placeholder="Round title — e.g. The night-shift breakdown"
                              aria-label={`Round ${i + 1} title`}
                              className={inputCls}
                            />
                            <textarea
                              rows={3}
                              value={r.context}
                              onChange={(e) => changeRound(i, 'context', e.target.value)}
                              placeholder="The situation — what has just happened, when, and what is at stake. e.g. At 2am the main filler jams with a rush order due at 6am. Maintenance says it needs four hours; Production says the truck cannot wait."
                              aria-label={`Round ${i + 1} situation`}
                              className={`${inputCls} resize-y`}
                            />
                            <input
                              value={r.challenge}
                              onChange={(e) => changeRound(i, 'challenge', e.target.value)}
                              placeholder="The decision teams must make (optional — the AI writes it if you leave this empty)"
                              aria-label={`Round ${i + 1} decision`}
                              className={inputCls}
                            />
                            {errors[`round_${i}`] && (
                              <p className="text-xs text-red-600 dark:text-red-400">{errors[`round_${i}`]}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRound(i)}
                            disabled={form.rounds.length <= MIN_ROUNDS}
                            className="mt-1 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-neutral-800"
                            aria-label={`Remove round ${i + 1}`}
                          >
                            <X size={15} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addRound}
                    disabled={form.rounds.length >= MAX_ROUNDS}
                    className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2.5 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 disabled:opacity-40 dark:border-neutral-700 dark:hover:border-neutral-600"
                  >
                    <Plus size={14} strokeWidth={2} className="mr-1 inline" />
                    {form.rounds.length >= MAX_ROUNDS ? 'Six rounds is the most a session can hold' : 'Add a round'}
                  </button>
                </div>

                <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    How long have you got?
                  </h3>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Deciding time per round</label>
                      <select value={form.minutesPerRound} onChange={(e) => patch({ minutesPerRound: Number(e.target.value) })} className={`${inputCls} mt-2`}>
                        {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} minutes</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Negotiating time per round</label>
                      <select
                        value={form.negotiateMinutes}
                        onChange={(e) => patch({ negotiateMinutes: Number(e.target.value) })}
                        disabled={!form.negotiateEnabled}
                        className={`${inputCls} mt-2 disabled:opacity-50`}
                      >
                        {[2, 3, 5, 8].map((n) => <option key={n} value={n}>{n} minutes</option>)}
                      </select>
                    </div>
                  </div>

                  <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-xl bg-neutral-50 p-3 dark:bg-neutral-800/60">
                    <input
                      type="checkbox"
                      checked={form.negotiateEnabled}
                      onChange={(e) => patch({ negotiateEnabled: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-400"
                    />
                    <span className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">
                      <span className="font-semibold text-neutral-800 dark:text-neutral-100">Give teams time to negotiate before they decide.</span>{' '}
                      Strongly recommended. Teams go and talk to each other face to face while a
                      countdown runs on every screen. Without it, &ldquo;collaborate&rdquo; is a button
                      people press rather than something they actually do.
                    </span>
                  </label>

                  <p className="mt-3 text-xs text-neutral-400">
                    Roughly {form.rounds.length * (form.minutesPerRound + (form.negotiateEnabled ? form.negotiateMinutes : 0) + 6)} minutes
                    of session time, including the reveal after each round and the final debrief.
                    The countdowns never move the room on by themselves — you do.
                  </p>
                </div>

                <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                  <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    <FileText size={16} strokeWidth={1.75} className="text-neutral-400" />
                    Reference documents
                    <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500 dark:bg-neutral-800">
                      Optional
                    </span>
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Process docs, incident reports, an existing workshop deck — anything that makes
                    the scenarios sound like your plant rather than a generic one.
                  </p>

                  <div className="mt-3 rounded-xl border-2 border-dashed border-neutral-300 p-6 text-center dark:border-neutral-700">
                    <Upload size={20} strokeWidth={1.5} className="mx-auto text-neutral-300" />
                    <p className="mt-2 text-xs font-medium text-neutral-500">Upload arrives in phase 5</p>
                    <p className="mt-0.5 text-[11px] text-neutral-400">
                      PDF, Word, Excel and PowerPoint. You&apos;ll review the extracted text before
                      the AI sees it.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        )}

        {/* ---------- nav ---------- */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => ((promptOnly || step === 1) ? navigate('/trainer/simulations/team') : setStep(step - 1))}
          >
            <ArrowLeft size={15} strokeWidth={2} className="mr-1.5" />
            {(promptOnly || step === 1) ? 'Cancel' : 'Back'}
          </Button>

          <div className="flex flex-wrap gap-2">
            {promptOnly ? (
              <Button variant="primary-filled" onClick={generate} loading={generating} disabled={generating}>
                <Sparkles size={15} strokeWidth={2} className="mr-1.5" />
                {generating ? 'Writing…' : 'Write my simulation'}
              </Button>
            ) : (
              step < LAST_FORM_STEP ? (
                <Button variant="primary-filled" onClick={next}>
                  Next
                  <ArrowRight size={15} strokeWidth={2} className="ml-1.5" />
                </Button>
              ) : (
                <Button variant="primary-filled" onClick={generate} loading={generating} disabled={generating}>
                  <Sparkles size={15} strokeWidth={2} className="mr-1.5" />
                  {generating ? 'Writing the decision deck…' : 'Write the decision deck'}
                </Button>
              )
            )}
          </div>
        </div>

        <p className="text-center text-xs text-neutral-400">
          {promptOnly
            ? 'Takes about two minutes. You can leave the page — it keeps writing, and you edit the whole thing before it goes live.'
            : 'Step 5: the AI writes the decision deck (each team’s options), their point of view in every round, the outside events and the debrief — about two minutes. Your rounds stay as you wrote them; you review and edit the deck before running it.'}
        </p>
      </div>
    </DashboardLayout>
  );
};

export default SimulationIntakePage;
